import classNames from "classnames";
import { Cell, BarChart, XAxis, YAxis, Bar, ResponsiveContainer, Tooltip, ReferenceArea } from "recharts";
import { FunctionComponent, memo, useMemo } from "react";
import { showServerError } from "utilities/show-server-error";
import { SpinnerUntil } from "@edgetier/components";
import { TimePeriodGroupBy } from "@edgetier/watchtower-types";
import useInteractionsCounts from "hooks-for/interactions/use-interactions-counts";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";

import PhraseCountsChartTooltip from "./phrase-counts-chart-tooltip";
import { formatChartData, upperXAxisTickInterval, applyProportionToQueryData } from "./phrase-counts-chart.utilities";
import { IPhraseChartData, IProps } from "./phrase-counts-chart.types";
import MaximumChartData from "components-for/charts/maximum-chart-data";
import { dateTimeUpperXAxisTickFormatter } from "utilities-for/charts/date-time-upper-x-axis-tick-formatter";
import "./phrase-counts-chart.scss";
import DateTimeLowerAxisTickFormatter from "components-for/charts/date-time-lower-axis-tick-formatter";

/**
 * Chart of how many interactions contained a phrase or phrases in a given time period.
 * @param props.anomalyId              The backend marks which interactions are part of an anomaly.
 * @param props.endDateTime            Closing date in search range.
 * @param props.filterRange            Time period of custom area.
 * @param props.groupBy                Time period.
 * @param props.height                 Optional height of the chart. Mostly for testing.
 * @param props.highlightEndDateTime   End date for highlighted area on chart.
 * @param props.highlightRange         Mark the highlighted area.
 * @param props.highlightStartDateTime Start date for highlighted area on chart.
 * @param props.isPreview              Boolean indicating if the chart is used for a preview.
 * @param props.mandatoryParameters     Interaction parameters that will be used regardless.
 * @param props.mouseCallbacks         Event callbacks for mousedown, mousemove and mouseup
 * @param props.startDateTime          Opening date in search range.
 * @param props.width                  Optional width of the chart. Mostly for testing.
 */
const PhraseCountsChart: FunctionComponent<IProps> = ({
    anomalyId,
    endDateTime,
    filterRange,
    groupBy,
    height = "100%",
    highlightEndDateTime,
    highlightRange = true,
    highlightStartDateTime,
    isPreview = false,
    mandatoryParameters,
    mouseCallbacks,
    proportion = false,
    startDateTime,
    width = "100%",
}) => {
    const interactionQueryParameters = useInteractionQueryParameters({
        defaults: { anomalyId, endDateTime, groupBy, startDateTime },
    });

    // Get counts of phrases or interactions according to the parameters.
    const { data: filteredData, isLoading: isFilteredDataLoading } = useInteractionsCounts(
        {
            ...interactionQueryParameters,
            ...mandatoryParameters,
        },
        {
            retry: !isPreview,
            onError: showServerError,
        }
    );

    // Get counts of phrases or interactions only by time period group
    const { data: genericData, isLoading: isGenericDataLoading } = useInteractionsCounts(
        {
            groupBy: interactionQueryParameters.groupBy,
            startDateTime: interactionQueryParameters.startDateTime,
            endDateTime: interactionQueryParameters.endDateTime,
        },
        {
            enabled: proportion,
            retry: !isPreview,
            onError: showServerError,
        }
    );

    // Apply proportion values
    const filteredProportionData = useMemo(
        () => applyProportionToQueryData(filteredData, genericData),
        [filteredData, genericData]
    );

    /**
     * Find out which bars to highlight.
     * Datetime is displayed as epoch time for chart selection
     */
    const chartData: IPhraseChartData[] = useMemo(
        () =>
            formatChartData(
                filteredProportionData,
                highlightRange,
                highlightStartDateTime,
                highlightEndDateTime,
                groupBy
            ),
        [highlightRange, highlightEndDateTime, highlightStartDateTime, filteredProportionData, groupBy]
    );

    return (
        <div className="phrase-counts-chart">
            <SpinnerUntil<typeof chartData> data={chartData} isReady={!isFilteredDataLoading && !isGenericDataLoading}>
                {(data) => (
                    <MaximumChartData dataLength={data.length}>
                        <ResponsiveContainer height={height} width={width}>
                            <BarChart data={data} {...mouseCallbacks}>
                                {!isPreview && (
                                    <XAxis
                                        xAxisId={0}
                                        interval={upperXAxisTickInterval(groupBy)}
                                        dataKey="dateTime"
                                        tickFormatter={(tickValue) =>
                                            dateTimeUpperXAxisTickFormatter(startDateTime, tickValue, groupBy)
                                        }
                                        tick={{
                                            fontSize:
                                                groupBy === TimePeriodGroupBy.Minutes ||
                                                groupBy === TimePeriodGroupBy.Hours
                                                    ? 10
                                                    : undefined,
                                        }}
                                    />
                                )}

                                {!isPreview && groupBy !== TimePeriodGroupBy.Months && (
                                    <XAxis
                                        xAxisId={1}
                                        axisLine={false}
                                        dataKey="dateTime"
                                        scale="band"
                                        interval={0}
                                        tick={(tickValue) => (
                                            <DateTimeLowerAxisTickFormatter
                                                tickValue={tickValue}
                                                dataLength={data.length}
                                                timePeriodGroup={groupBy}
                                            />
                                        )}
                                    />
                                )}

                                {!isPreview && (
                                    <YAxis
                                        yAxisId={0}
                                        tickFormatter={(value) => (proportion ? `${(value * 100).toFixed(1)}%` : value)}
                                    />
                                )}

                                <Bar dataKey={proportion ? "proportion" : "count"} radius={5}>
                                    {data.map(({ dateTime, isHighlighted }) => (
                                        <Cell
                                            key={dateTime}
                                            className={classNames({
                                                "phrase-counts-chart--is-highlighted": isHighlighted && highlightRange,
                                                "phrase-counts-chart--is-neutral": !highlightRange,
                                            })}
                                        />
                                    ))}
                                </Bar>
                                <ReferenceArea
                                    fill={"#1abc9c"}
                                    fillOpacity={0.3}
                                    x1={filterRange?.[0].activeLabel ?? ""}
                                    x2={filterRange?.[1].activeLabel ?? ""}
                                />

                                <Tooltip
                                    content={({ active, payload }) =>
                                        active &&
                                        Array.isArray(payload) &&
                                        typeof payload[0].value === "number" && (
                                            <PhraseCountsChartTooltip
                                                startDateTime={startDateTime}
                                                endDateTime={endDateTime!}
                                                dateTime={payload[0].payload.dateTime}
                                                proportion={proportion}
                                                timePeriodGroup={groupBy}
                                                value={payload[0].value}
                                            />
                                        )
                                    }
                                    isAnimationActive={false}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </MaximumChartData>
                )}
            </SpinnerUntil>
        </div>
    );
};

export default memo(PhraseCountsChart);
